import React, { useState, useEffect } from 'react';
import { X, Database, HardDrive, Cloud, RefreshCw, CheckCircle, AlertCircle, Info } from 'lucide-react';

/**
 * PlacesSyncDebugPopup - Affiche les résultats de synchronisation des places au login
 * S'affiche automatiquement via window.showPlacesSyncDebug() appelé dans authService
 */
const PlacesSyncDebugPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);

  useEffect(() => {
    console.log('[PlacesSyncDebugPopup] Mounting component, setting window.showPlacesSyncDebug');

    // Expose la fonction globale pour afficher le popup
    // Use a ref-like pattern to always call the latest setState
    window.showPlacesSyncDebug = (places) => {
      console.log('[PlacesSyncDebugPopup] showPlacesSyncDebug called with', places?.length || 0, 'places');
      // Récupérer les données de debug depuis le service
      import('../../services/userPlacesService').then(module => {
        const debug = module.getLastSyncDebug();
        console.log('[PlacesSyncDebugPopup] Debug data:', debug);
        setDebugData({
          ...debug,
          places: places || []
        });
        setIsOpen(true);
      });
    };

    console.log('[PlacesSyncDebugPopup] window.showPlacesSyncDebug is now:', typeof window.showPlacesSyncDebug);

    // Ne PAS supprimer la fonction au unmount car React StrictMode peut causer des problèmes de timing
    return () => {
      console.log('[PlacesSyncDebugPopup] Unmounting (keeping window.showPlacesSyncDebug)');
    };
  }, []);

  if (!isOpen || !debugData) return null;

  const getSourceIcon = (source) => {
    if (source.includes('local')) return <HardDrive className="w-4 h-4" />;
    if (source.includes('server')) return <Cloud className="w-4 h-4" />;
    if (source.includes('API')) return <Database className="w-4 h-4" />;
    return <Info className="w-4 h-4" />;
  };

  const getSourceColor = (source) => {
    if (source.includes('local')) return 'text-blue-400';
    if (source.includes('server')) return 'text-purple-400';
    if (source.includes('API')) return 'text-orange-400';
    return 'text-gray-400';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
      <div className="bg-gray-900 text-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-4 bg-gray-800 border-b border-gray-700 flex justify-between items-center">
          <h3 className="font-mono text-lg flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-green-400" />
            Places Sync Debug
          </h3>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <Database className="w-6 h-6 mx-auto mb-1 text-orange-400" />
              <div className="text-2xl font-bold text-orange-400">{debugData.dbCount}</div>
              <div className="text-xs text-gray-400">DB Count</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <HardDrive className="w-6 h-6 mx-auto mb-1 text-blue-400" />
              <div className="text-2xl font-bold text-blue-400">{debugData.localCount}</div>
              <div className="text-xs text-gray-400">localStorage</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <Cloud className="w-6 h-6 mx-auto mb-1 text-purple-400" />
              <div className="text-2xl font-bold text-purple-400">{debugData.serverFileCount}</div>
              <div className="text-xs text-gray-400">Server File</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-1 text-green-400" />
              <div className="text-2xl font-bold text-green-400">{debugData.finalCount}</div>
              <div className="text-xs text-gray-400">Final</div>
            </div>
          </div>

          {/* Source Used */}
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-gray-400 text-sm">Source utilisée:</span>
              <span className={`flex items-center gap-1 font-mono ${getSourceColor(debugData.source)}`}>
                {getSourceIcon(debugData.source)}
                {debugData.source || 'N/A'}
              </span>
            </div>
            {debugData.syncChanges !== 0 && (
              <div className="text-sm text-yellow-400">
                ⚠️ {debugData.syncChanges > 0 ? '+' : ''}{debugData.syncChanges} changements après sync DB
              </div>
            )}
          </div>

          {/* Errors */}
          {debugData.errors && debugData.errors.length > 0 && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-400 mb-2">
                <AlertCircle className="w-4 h-4" />
                <span className="font-semibold">Erreurs</span>
              </div>
              {debugData.errors.map((err, i) => (
                <div key={i} className="text-sm text-red-300 font-mono">{err}</div>
              ))}
            </div>
          )}

          {/* Steps Log */}
          <div className="bg-gray-800 rounded-lg p-3">
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Étapes de synchronisation:</h4>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {debugData.steps && debugData.steps.map((step, i) => (
                <div key={i} className="text-xs font-mono flex items-start gap-2">
                  <span className="text-gray-500 shrink-0">
                    {new Date(step.time).toLocaleTimeString()}
                  </span>
                  <span className={step.step.includes('ERROR') ? 'text-red-400' : 'text-green-400'}>
                    {step.step}
                  </span>
                  {step.details && Object.keys(step.details).length > 0 && (
                    <span className="text-gray-400">
                      {JSON.stringify(step.details)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Places List */}
          {debugData.places && debugData.places.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-gray-300 mb-2">
                Places synchronisés ({debugData.places.length}):
              </h4>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {debugData.places.map((place, i) => (
                  <div key={place.localUuid || i} className="text-xs font-mono flex items-center gap-2 py-1 border-b border-gray-700 last:border-0">
                    <span className="text-gray-500 w-6">{i + 1}.</span>
                    <span className="text-cyan-400 flex-1 truncate" title={place.placeName}>
                      {place.placeName || 'Sans nom'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      place.role === 'PUBLIC' ? 'bg-green-900 text-green-300' :
                      place.role === 'PRIVATE' ? 'bg-purple-900 text-purple-300' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {place.role || 'CONTACT'}
                    </span>
                    {place.placeUuid && (
                      <span className="text-orange-400" title={`placeUuid: ${place.placeUuid}`}>
                        🔗
                      </span>
                    )}
                    {place.isOwner && (
                      <span className="text-yellow-400" title="Owner">
                        👑
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw Debug Data (collapsible) */}
          <details className="bg-gray-800 rounded-lg">
            <summary className="p-3 cursor-pointer text-gray-400 text-sm hover:text-white">
              Voir données brutes JSON
            </summary>
            <pre className="p-3 pt-0 text-xs text-gray-300 overflow-x-auto max-h-60">
              {JSON.stringify(debugData, null, 2)}
            </pre>
          </details>
        </div>

        {/* Footer */}
        <div className="p-3 bg-gray-800 border-t border-gray-700 flex justify-between items-center">
          <span className="text-xs text-gray-500">
            {debugData.timestamp ? new Date(debugData.timestamp).toLocaleString() : ''}
          </span>
          <button
            onClick={() => setIsOpen(false)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 text-sm"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlacesSyncDebugPopup;
